import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, X, ArrowRight } from "lucide-react";

export default function Quiz() {
  const { id } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/quizzes/${id}`).then(({ data }) => setQuiz(data));
  }, [id]);

  if (!quiz) return <div className="p-10 font-mono-label text-muted-foreground">Loading…</div>;

  const q = quiz.questions[idx];
  const isLast = idx === quiz.questions.length - 1;

  const pick = (i) => {
    const next = [...answers];
    next[idx] = i;
    setAnswers(next);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/quizzes/${id}/attempt`, { answers });
      setResult(data);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-16" data-testid="quiz-results">
        <div className="font-mono-label text-primary">Assessment Results</div>
        <h1 className="font-serif-display text-5xl md:text-6xl mt-3 font-light leading-none tracking-tighter">{quiz.title}</h1>
        <div className="mt-10 porcelain-card p-8">
          <div className="flex items-end justify-between">
            <div>
              <div className="font-mono-label text-muted-foreground">Your Score</div>
              <div className="font-serif-display text-8xl leading-none mt-2">
                <span className={result.percent >= 70 ? "text-primary" : ""}>{result.percent}%</span>
              </div>
            </div>
            <div className="font-mono text-2xl text-muted-foreground">{result.score} / {result.total}</div>
          </div>
        </div>

        <div className="mt-12 space-y-4">
          {result.details.map((d, i) => (
            <div key={i} className={`p-6 border ${d.is_correct ? "border-primary/30 bg-primary/5" : "border-destructive/30 bg-destructive/5"}`} style={{ borderRadius: 2 }}>
              <div className="flex items-start gap-3">
                {d.is_correct ? <Check className="w-5 h-5 text-primary mt-0.5" /> : <X className="w-5 h-5 text-destructive mt-0.5" />}
                <div className="flex-1">
                  <div className="font-serif-display text-xl">{quiz.questions[i].prompt}</div>
                  <div className="mt-3 text-sm">
                    <div className="text-muted-foreground">Correct: <span className="text-foreground">{quiz.questions[i].options[d.correct_index]}</span></div>
                    <div className="mt-2 text-muted-foreground italic">{d.explanation}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 flex gap-4">
          <Link to="/dashboard" className="btn-oxblood text-sm" data-testid="back-dashboard">Back to dashboard</Link>
          <Link to={`/lessons/${quiz.lesson_id}`} className="btn-outline-mono text-sm" data-testid="back-lesson">Review lesson</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[800px] mx-auto px-6 py-16" data-testid="quiz-page">
      <div className="flex items-center justify-between mb-10">
        <div className="font-mono-label text-primary">{quiz.title}</div>
        <div className="font-mono-label text-muted-foreground">{idx + 1} / {quiz.questions.length}</div>
      </div>

      <div className="h-1 bg-secondary mb-12">
        <div className="h-full bg-primary transition-all" style={{ width: `${((idx + 1) / quiz.questions.length) * 100}%` }} />
      </div>

      <h2 className="font-serif-display text-4xl md:text-5xl font-light leading-tight tracking-tight">{q.prompt}</h2>

      <div className="mt-10 space-y-3">
        {q.options.map((opt, i) => (
          <button
            key={i}
            onClick={() => pick(i)}
            data-testid={`quiz-option-${i}`}
            className={`w-full text-left p-5 border transition-all ${
              answers[idx] === i
                ? "border-primary bg-primary/5"
                : "border-border hover:border-foreground bg-white"
            }`}
            style={{ borderRadius: 2 }}
          >
            <div className="flex items-center gap-4">
              <div className={`font-mono w-8 h-8 border flex items-center justify-center ${answers[idx] === i ? "border-primary text-primary" : "border-border text-muted-foreground"}`} style={{ borderRadius: 2 }}>
                {String.fromCharCode(65 + i)}
              </div>
              <div className="text-base">{opt}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-10 flex justify-between">
        <button
          onClick={() => setIdx(Math.max(0, idx - 1))}
          disabled={idx === 0}
          className="btn-outline-mono text-sm disabled:opacity-30"
          data-testid="quiz-prev"
        >
          Previous
        </button>
        {isLast ? (
          <button
            onClick={submit}
            disabled={answers[idx] === undefined || submitting}
            className="btn-oxblood text-sm disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="quiz-submit"
          >
            {submitting ? "Grading…" : "Submit"} <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={() => setIdx(idx + 1)}
            disabled={answers[idx] === undefined}
            className="btn-oxblood text-sm disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="quiz-next"
          >
            Next <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
